/**
 * @file MeetingShareItems.tsx
 * @brief "Share…" and "Who has access" in a meeting card's "⋯" menu.
 *
 * @details
 * DES 22-10-2026: the actions of the meeting page's Share pill, from every
 * meeting card and row (Home, Meetings, Notes, Shared with me), Boardroom
 * and Notes alike. Both open the same panel as the pill and the card's
 * "Shared with" line (`MeetingShareMenu`), as a submenu of the "⋯" menu.
 * - Who sees them comes from the list item's `sharing.viewerRole`
 *   (`meetingShareRights`, pure, tested): the owner gets both, a company
 *   admin on a member's meeting gets "Who has access" (remove only); a
 *   shared person, an invitee or a to-do-only member gets neither (the
 *   server answers them 404 on /access).
 * - Sharing switched off (`BOARDROOM_SHARE_AFTER=false`, every share route
 *   404s): the list still carries `sharing`, so the access list is read
 *   when the menu opens (the same read the panel needs) and a 404 hides
 *   both items.
 * - Company data controls (ADR-051) and canAdd / canRemove are the panel's
 *   own: an inside-only company's line, outsiders not offered, admin
 *   removes only.
 * After an add or remove the panel refreshes every meetings list, so the
 * card's "Shared with" line follows at once.
 *
 * Cards with no "⋯" menu of their own (Home's meeting cards, the Notes
 * page's meeting rows) get `MeetingCardShareMenu`: the Notes Library rows'
 * "⋯", shown on hover, holding just these two items, and only for a viewer
 * who has them.
 *
 * No vendor cost: one database read when a menu opens.
 *
 * @references
 * - ./meetingListTools.tsx (MeetingShareMenu, useMeetingAccess)
 * - ./sharingLine.ts (meetingShareRights)
 * - scratch/claude-code-report.md "Sharing v2: Part 1" section 3
 *
 * @author DES
 * @date 22-10-2026
 */

import { useState, type KeyboardEvent, type MouseEvent } from "react";
import { MoreHorizontal, Share2, Users } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@/components/ui/DropdownMenu";
import { ApiClientError } from "@/shared/api/client";
import { cn } from "@/shared/theme/cn";
import type { MeetingSharing } from "@/features/company-meetings/schemas";
import { MeetingShareMenu, useMeetingAccess } from "./meetingListTools";
import { meetingShareRights } from "./sharingLine";

/** Render inside an open `DropdownMenuContent`. Draws nothing for a viewer
 *  who may not share or see the list. */
export function MeetingShareItems({
  meetingId,
  sharing,
  separator = false,
}: {
  meetingId: string;
  sharing: MeetingSharing | null | undefined;
  /** A separator after the items, drawn only with them. */
  separator?: boolean;
}) {
  const rights = meetingShareRights(sharing);
  const show = rights.canShare || rights.canSeeAccess;
  // Mounted only while the menu is open: one read, which the panel reuses.
  const access = useMeetingAccess(meetingId, show);
  if (!show) return null;
  if (access.error instanceof ApiClientError && access.error.status === 404) return null;
  return (
    <>
      {rights.canShare ? (
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <Share2 className="mr-2 h-4 w-4" />
            Share…
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="p-1">
            <MeetingShareMenu meetingId={meetingId} />
          </DropdownMenuSubContent>
        </DropdownMenuSub>
      ) : null}
      <DropdownMenuSub>
        <DropdownMenuSubTrigger>
          <Users className="mr-2 h-4 w-4" />
          Who has access
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent className="p-1">
          <MeetingShareMenu meetingId={meetingId} listOnly />
        </DropdownMenuSubContent>
      </DropdownMenuSub>
      {separator ? <DropdownMenuSeparator /> : null}
    </>
  );
}

const stop = (e: MouseEvent | KeyboardEvent) => {
  e.stopPropagation();
};

/** A "⋯" with only the two items, for a card that is a link (its clicks and
 *  keys stay here, as `MeetingSharingLine`'s). Nothing for anyone else. */
export function MeetingCardShareMenu({
  meetingId,
  sharing,
  title,
  className,
}: {
  meetingId: string;
  sharing: MeetingSharing | null | undefined;
  /** The meeting's title, for the button's name. */
  title: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  if (!meetingShareRights(sharing).canSeeAccess) return null;
  return (
    <span
      className={cn("flex shrink-0", className)}
      onClick={stop}
      onKeyDown={stop}
      onPointerDown={stop}
    >
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <span
            role="button"
            tabIndex={0}
            aria-label={`Options for ${title}`}
            onClick={(e) => e.preventDefault()}
            className={cn(
              "flex h-7 w-7 cursor-pointer items-center justify-center rounded-full text-muted-foreground outline-none transition-opacity hover:bg-secondary focus-visible:ring-2 focus-visible:ring-ring",
              "opacity-0 focus-visible:opacity-100 group-hover:opacity-100",
              open && "opacity-100",
            )}
          >
            <MoreHorizontal className="h-5 w-5" />
          </span>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <MeetingShareItems meetingId={meetingId} sharing={sharing} />
        </DropdownMenuContent>
      </DropdownMenu>
    </span>
  );
}
